import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { fetchAccounts } from '../../services/api'
import Loader from '../../components/Loader'
import Pagination from '../../components/Pagination'
import {
	Alert,
	Badge,
	Button,
	Card,
	EmptyState,
	PageHeader,
	Select,
	TextInput,
	statusTone,
} from '../../components/ui'

const SPECIAL_ACCOUNT_TYPES = [
	{ value: '', label: 'Todos' },
	{ value: 'STANDARD_ACCOUNT', label: 'Normal' },
	{ value: 'PAYING_AGENT', label: 'Pagadoria' },
]

const specialAccountTypeLabel = (value) =>
	SPECIAL_ACCOUNT_TYPES.find((option) => option.value === value)?.label || '—'

const emptyFilters = { number: '', accountHolderName: '', specialType: '' }

const AccountsList = () => {
	const navigate = useNavigate()
	const [filters, setFilters] = useState(emptyFilters)
	const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
	const [page, setPage] = useState(1)
	const [data, setData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		try {
			const response = await fetchAccounts({
				...appliedFilters,
				page,
				perPage: 10,
				orderColumn: 'id',
				orderDirection: 'ASC',
			})
			setData(response)
		} catch (err) {
			setError(err.message)
			setData(null)
		} finally {
			setIsLoading(false)
		}
	}, [appliedFilters, page])

	useEffect(() => {
		load()
	}, [load])

	const handleFilterChange = (e) => {
		const { name, value } = e.target
		setFilters((prev) => ({ ...prev, [name]: value }))
	}

	const handleSubmit = (e) => {
		e.preventDefault()
		setPage(1)
		setAppliedFilters(filters)
	}

	const handleClear = () => {
		setFilters(emptyFilters)
		setAppliedFilters(emptyFilters)
		setPage(1)
	}

	const accounts = data?.content || []

	return (
		<div>
			<PageHeader
				title="Contas"
				subtitle="GET /backoffice/accounts — resumo das contas cadastradas"
			/>

			<Card className="mb-6">
				<form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
					<TextInput
						id="number"
						name="number"
						label="Numero da conta"
						placeholder="Ex.: 123456"
						value={filters.number}
						onChange={handleFilterChange}
					/>
					<TextInput
						id="accountHolderName"
						name="accountHolderName"
						label="Titular"
						placeholder="Nome do titular"
						value={filters.accountHolderName}
						onChange={handleFilterChange}
					/>
					<Select
						id="specialType"
						name="specialType"
						label="Tipo de conta"
						options={SPECIAL_ACCOUNT_TYPES}
						value={filters.specialType}
						onChange={handleFilterChange}
					/>
					<div className="flex items-end gap-2">
						<Button type="submit">
							<Search className="h-4 w-4" />
							Buscar
						</Button>
						<Button type="button" variant="secondary" onClick={handleClear}>
							<X className="h-4 w-4" />
							Limpar
						</Button>
					</div>
				</form>
			</Card>

			<Alert onDismiss={() => setError('')}>{error}</Alert>

			<div className="overflow-hidden rounded-lg bg-white shadow">
				{isLoading ? (
					<div className="flex h-64 items-center justify-center">
						<Loader size="lg" />
					</div>
				) : accounts.length === 0 ? (
					<EmptyState message="Nenhuma conta encontrada" />
				) : (
					<>
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Numero
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Titular
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Tipo
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Status
										</th>
										<th className="px-6 py-3" />
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
									{accounts.map((account) => (
										<tr key={account.id} className="hover:bg-gray-50">
											<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
												{account.number || '—'}
											</td>
											<td className="px-6 py-4 text-sm text-gray-900">
												{account.accountHolder?.name || '—'}
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
												{specialAccountTypeLabel(account.specialType)}
											</td>
											<td className="whitespace-nowrap px-6 py-4">
												<Badge tone={statusTone(account.status)}>
													{account.status || '—'}
												</Badge>
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-right">
												<Button
													variant="secondary"
													onClick={() => navigate(`/contas/${account.id}`)}
												>
													Detalhes
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<Pagination
							currentPage={data?.currentPage || page}
							totalPages={data?.totalPages || 1}
							onPageChange={setPage}
						/>
					</>
				)}
			</div>
		</div>
	)
}

export default AccountsList
